import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, RefreshCw, RotateCcw, Search } from "lucide-react";
import { OrderLink } from "../components/OrderLink";
import { Button, EmptyState, ErrorBanner, LoadingState, Modal, PageHeader, StatusBadge } from "../components/Ui";
import { useAuth } from "../context/AuthContext";
import { requireSupabase } from "../lib/supabase";
import { formatDateTime } from "../lib/warehouseData";
import { useDeveloperSettings } from "../lib/developerSettings";

type ReturnRow = {
  id: string;
  work_order_id: string;
  warehouse_id: string;
  status: "registered" | "picked_up";
  registration_note: string | null;
  registered_at: string;
  pickup_note: string | null;
  picked_up_at: string | null;
  orderNumber: string;
  warehouseName: string;
};

export function ReturnsPage() {
  const { cloudConfigured } = useAuth();
  const { exampleTexts } = useDeveloperSettings();
  const [rows, setRows] = useState<ReturnRow[]>([]);
  const [loading, setLoading] = useState(cloudConfigured);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReturnRow | null>(null);
  const [pickupNote, setPickupNote] = useState("");
  const [filter, setFilter] = useState<"all" | "registered" | "picked_up">("all");
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    if (!cloudConfigured) return;
    setLoading(true);
    setError("");
    try {
      const client = requireSupabase();
      const [returnResult, orderResult, warehouseResult] = await Promise.all([
        client.from("order_returns").select("id,work_order_id,warehouse_id,status,registration_note,registered_at,pickup_note,picked_up_at").order("registered_at", { ascending: false }),
        client.from("work_orders").select("id,order_number"),
        client.from("warehouses").select("id,name")
      ]);
      const firstError = returnResult.error || orderResult.error || warehouseResult.error;
      if (firstError) throw firstError;
      const orders = new Map((orderResult.data ?? []).map((item) => [item.id, item.order_number]));
      const warehouses = new Map((warehouseResult.data ?? []).map((item) => [item.id, item.name]));
      setRows((returnResult.data ?? []).map((item) => ({
        ...item,
        status: item.status as ReturnRow["status"],
        orderNumber: orders.get(item.work_order_id) ?? "–",
        warehouseName: warehouses.get(item.warehouse_id) ?? "Unbekanntes Lager"
      })));
    } catch (nextError) {
      setError(`Retouren konnten nicht geladen werden: ${nextError instanceof Error ? nextError.message : "Unbekannter Fehler"}`);
    } finally {
      setLoading(false);
    }
  }, [cloudConfigured]);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("de-DE");
    return rows.filter((row) => {
      if (filter !== "all" && row.status !== filter) return false;
      if (!term) return true;
      return [row.orderNumber, row.warehouseName, row.registration_note ?? "", row.pickup_note ?? ""]
        .some((value) => value.toLocaleLowerCase("de-DE").includes(term));
    });
  }, [filter, rows, search]);

  async function completeReturn() {
    if (!selected) return;
    setBusyId(selected.id);
    setError("");
    setMessage("");
    try {
      const { error: rpcError } = await requireSupabase().rpc("complete_order_return", {
        target_return_id: selected.id,
        target_note: pickupNote.trim() || null
      });
      if (rpcError) throw rpcError;
      setMessage(`Die Retoure für ${selected.orderNumber} wurde als abgeholt protokolliert.`);
      setSelected(null);
      setPickupNote("");
      await load();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Retourenabholung konnte nicht gespeichert werden.");
    } finally {
      setBusyId(null);
    }
  }

  const openCount = rows.filter((row) => row.status === "registered").length;
  const completedCount = rows.filter((row) => row.status === "picked_up").length;

  return (
    <>
      <PageHeader title="Retouren" description="Offene und abgeholte Retouren beider freigegebenen Lager." action={<Button variant="secondary" onClick={() => void load()} disabled={loading}><RefreshCw size={16} /> Aktualisieren</Button>} />
      {error && <ErrorBanner message={error} />}
      {message && <div className="success-banner" role="status"><CheckCircle2 size={17} /><span>{message}</span></div>}
      {!cloudConfigured && <div className="notice-banner">Retouren werden erst mit verbundener Testcloud angezeigt.</div>}

      <section className="return-stat-grid">
        <button type="button" className={filter === "registered" ? "return-stat return-stat--active" : "return-stat"} onClick={() => setFilter(filter === "registered" ? "all" : "registered")}><span><RotateCcw size={20} /></span><small>Offen</small><strong>{openCount}</strong></button>
        <button type="button" className={filter === "picked_up" ? "return-stat return-stat--active return-stat--complete" : "return-stat return-stat--complete"} onClick={() => setFilter(filter === "picked_up" ? "all" : "picked_up")}><span><CheckCircle2 size={20} /></span><small>Abgeholt</small><strong>{completedCount}</strong></button>
      </section>

      <div className="toolbar"><label className="search-box"><Search size={18} /><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={exampleTexts.returnsSearch} /></label></div>

      {loading ? <LoadingState label="Retouren werden geladen …" /> : filtered.length === 0 ? (
        <EmptyState title="Keine Retouren gefunden" description={rows.length === 0 ? "Retouren werden auf der jeweiligen Auftragsseite angemeldet." : "Bitte Suche oder Statusfilter anpassen."} />
      ) : (
        <div className="table-shell">
          <table>
            <thead><tr><th>Auftrag</th><th>Lager</th><th>Anmeldung</th><th>Status</th><th>Abholung</th><th>Aktion</th></tr></thead>
            <tbody>{filtered.map((row) => <tr key={row.id}>
              <td><strong><OrderLink orderNumber={row.orderNumber} /></strong>{row.registration_note && <small className="table-subline">{row.registration_note}</small>}</td>
              <td>{row.warehouseName}</td>
              <td>{formatDateTime(row.registered_at)}</td>
              <td><StatusBadge tone={row.status === "registered" ? "warning" : "success"}>{row.status === "registered" ? "Retoure angemeldet" : "Retoure abgeholt"}</StatusBadge></td>
              <td>{row.picked_up_at ? formatDateTime(row.picked_up_at) : "–"}{row.pickup_note && <small className="table-subline">{row.pickup_note}</small>}</td>
              <td>{row.status === "registered" ? <Button className="button--small" onClick={() => { setSelected(row); setPickupNote(""); }} disabled={busyId === row.id}><CheckCircle2 size={15} /> Abgeholt</Button> : <span className="muted-text">Erledigt</span>}</td>
            </tr>)}</tbody>
          </table>
        </div>
      )}

      {selected && (
        <Modal title="Retourenabholung bestätigen" description={`${selected.orderNumber} · ${selected.warehouseName}`} onClose={() => { if (!busyId) setSelected(null); }}>
          <div className="return-pickup-confirmation">
            <span><CheckCircle2 size={25} /></span>
            <div><strong>Wurde die Retoure vollständig abgeholt?</strong><p>Die Packstücke werden anschließend aus dem verfügbaren Lagerbestand entfernt.</p></div>
            <label className="field"><span>Notiz zur Abholung (optional)</span><textarea className="input return-textarea" maxLength={2000} value={pickupNote} onChange={(event) => setPickupNote(event.target.value)} placeholder={exampleTexts.optional} /></label>
            <div className="form-actions"><Button variant="secondary" onClick={() => setSelected(null)} disabled={Boolean(busyId)}>Abbrechen</Button><Button onClick={() => void completeReturn()} disabled={Boolean(busyId)}><CheckCircle2 size={16} /> Abholung bestätigen</Button></div>
          </div>
        </Modal>
      )}
    </>
  );
}
